import { Badge, buttonVariants, Card, CardContent, cn } from '@napaicp/ui'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { HomeAction } from '@/features/home/derive-home'
import { formatDateTimeTaipei } from '@/lib/format'

/**
 * "我的下一步" — the redesigned `/home`'s single core Acceptance Criterion:
 * a multi-role account sees the aggregated, prioritized work across every
 * scope it holds without picking "which role am I right now" first. Every
 * item here came out of `useHomeOverview()` already prioritized
 * (`sortActions`) — this component only renders, it never re-derives or
 * re-sorts.
 */

const PRIORITY_BADGE_VARIANT: Record<HomeAction['priority'], 'danger' | 'warning' | 'neutral'> = {
  p0: 'danger',
  p1: 'warning',
  p2: 'neutral',
}

function messageKeyFor(kind: HomeAction['kind']): string {
  switch (kind) {
    case 'admin_publish':
      return 'nextActions.item.adminPublish'
    case 'judge_pending':
      return 'nextActions.item.judgePending'
    case 'platform_governance':
      return 'nextActions.item.platformGovernance'
    case 'student_registration':
      return 'nextActions.item.studentRegistration'
    case 'student_registration_update':
      return 'nextActions.item.studentRegistrationUpdate'
    case 'student_team':
      return 'nextActions.item.studentTeam'
    case 'student_submission':
      return 'nextActions.item.studentSubmission'
  }
}

function ctaKeyFor(kind: HomeAction['kind']): string {
  switch (kind) {
    case 'admin_publish':
      return 'nextActions.cta.adminPublish'
    case 'judge_pending':
      return 'nextActions.cta.judgePending'
    case 'platform_governance':
      return 'nextActions.cta.platformGovernance'
    case 'student_registration':
      return 'nextActions.cta.studentRegistration'
    case 'student_registration_update':
      return 'nextActions.cta.studentRegistrationUpdate'
    case 'student_team':
      return 'nextActions.cta.studentTeam'
    case 'student_submission':
      return 'nextActions.cta.studentSubmission'
  }
}

export function NextActionsSection({ actions }: { actions: HomeAction[] }) {
  const { t } = useTranslation('home')

  return (
    <section className="flex flex-col gap-3" aria-labelledby="next-actions-heading">
      <h2 id="next-actions-heading" className="text-xl font-semibold tracking-tight">
        {t('nextActions.title')}
      </h2>
      {actions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col gap-2 pt-6">
            <p className="font-medium">{t('nextActions.empty.title')}</p>
            <p className="text-sm text-muted-foreground">{t('nextActions.empty.description')}</p>
            <Link
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit')}
              to="/#published-competitions"
            >
              {t('nextActions.empty.exploreCta')}
            </Link>
          </CardContent>
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {actions.map((action) => (
            <li key={action.key}>
              <Card className={action.priority === 'p0' ? 'border-destructive/50' : undefined}>
                <CardContent className="flex flex-col gap-1.5 pt-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={PRIORITY_BADGE_VARIANT[action.priority]}>
                      {t(`nextActions.priority.${action.priority}`)}
                    </Badge>
                    {action.competitionName !== null ? (
                      <span className="text-sm text-muted-foreground">
                        {action.competitionName}
                      </span>
                    ) : null}
                  </div>
                  <p className="font-medium">
                    {t(messageKeyFor(action.kind), {
                      count: action.count,
                      competitionName: action.competitionName ?? '',
                    })}
                  </p>
                  {action.deadline !== null ? (
                    <p className="text-sm text-muted-foreground">
                      {t('nextActions.deadline', {
                        date: formatDateTimeTaipei(action.deadline, { withTimezone: true }),
                      })}
                    </p>
                  ) : null}
                  <Link
                    className={cn(buttonVariants({ size: 'sm' }), 'mt-1 w-fit')}
                    to={action.ctaTo}
                  >
                    {t(ctaKeyFor(action.kind))}
                  </Link>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
